import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Warehouse, 
  Truck, 
  ShieldCheck, 
  FileText, 
  ArrowRight,
  Globe,
  CheckCircle
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';

// Hook for scroll-triggered animations
const useScrollAnimation = (threshold = 0.2) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
};

// Image carousel component
const ImageCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const images = [
    {
      url: "https://customer-assets.emergentagent.com/job_export-mediator/artifacts/v46pmry4_Container-entladen.webp",
      alt: "Container loading with forklift"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_export-mediator/artifacts/nspjn1qk_22bf60a6-eba2-4fe3-8fa0-dc1ca70502a2.avif",
      alt: "Warehouse with wooden crates"
    },
    {
      url: "https://customer-assets.emergentagent.com/job_export-mediator/artifacts/vq4gubh3_abv-banner-restposten.jpg.webp",
      alt: "Warehouse storage boxes"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <div className="relative w-full h-[400px] rounded-2xl overflow-hidden shadow-xl">
      {images.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={image.url}
            alt={image.alt}
            className="w-full h-full object-cover"
          />
        </div>
      ))}
      {/* Dots indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
              index === currentIndex 
                ? 'bg-white w-6' 
                : 'bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export const HomePage = () => {
  const { t, isRTL } = useLanguage();
  const [heroLoaded, setHeroLoaded] = useState(false);
  
  // Animation refs for different sections
  const servicesAnimation = useScrollAnimation(0.1);
  const whyChooseAnimation = useScrollAnimation(0.2);
  const ctaAnimation = useScrollAnimation(0.3);

  useEffect(() => {
    // Trigger hero animation after mount
    const timer = setTimeout(() => setHeroLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const services = [
    { icon: Warehouse, titleKey: 'services.service1Title', descKey: 'services.service1Desc' },
    { icon: Truck, titleKey: 'services.service2Title', descKey: 'services.service2Desc' },
    { icon: ShieldCheck, titleKey: 'services.service3Title', descKey: 'services.service3Desc' },
    { icon: FileText, titleKey: 'services.service4Title', descKey: 'services.service4Desc' },
  ];

  const features = [
    { icon: CheckCircle, text: t('about.point1Title') },
    { icon: CheckCircle, text: t('about.point2Title') },
    { icon: CheckCircle, text: t('about.point3Title') },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Image with moving ship animation */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div 
            className="absolute inset-0 w-[130%] h-full"
            style={{
              transform: heroLoaded 
                ? (isRTL ? 'translateX(10%)' : 'translateX(-15%)') 
                : (isRTL ? 'translateX(-5%)' : 'translateX(0%)'),
              transition: 'transform 20s linear',
              left: isRTL ? 'auto' : '-15%',
              right: isRTL ? '-15%' : 'auto',
            }}
          >
            <img
              src="https://images.unsplash.com/photo-1632517306067-b54ab4d1f98d?auto=format&fit=crop&w=2000&q=80"
              alt="Container ship"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/50" />
        </div>

        {/* Hero Content with staggered animations */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <h1 
              className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-heading leading-tight mb-6 transform transition-all duration-700 ${heroLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
              data-testid="hero-title"
            >
              {t('hero.title')}
            </h1>
            <p 
              className={`text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed transform transition-all duration-700 delay-200 ${heroLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
              data-testid="hero-subtitle"
            >
              {t('hero.subtitle')}
            </p>
            <div className={`flex flex-wrap gap-4 transform transition-all duration-700 delay-400 ${heroLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-primary-blue hover:bg-primary-blue/90 text-white px-8 hover:scale-105 transition-transform duration-200"
                  data-testid="hero-cta-primary"
                >
                  {t('hero.cta')}
                  <ArrowRight className={`w-5 h-5 ${isRTL ? 'me-2 rotate-180' : 'ms-2'}`} />
                </Button>
              </Link>
              <Link to="/about">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white text-white hover:bg-white/10 px-8 hover:scale-105 transition-transform duration-200"
                  data-testid="hero-cta-secondary"
                >
                  {t('hero.ctaSecondary')}
                </Button>
              </Link>
            </div>

            {/* Trust Badges with staggered animation */}
            <div className={`mt-12 flex flex-wrap gap-6 transform transition-all duration-700 delay-500 ${heroLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-2 text-slate-300"
                  style={{ animationDelay: `${600 + index * 100}ms` }}
                >
                  <feature.icon className="w-5 h-5 text-accent-red" />
                  <span className="text-sm font-medium">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Animated scroll indicator */}
        <div className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 transition-all duration-700 delay-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}>
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-white/70 rounded-full mt-2 animate-bounce" />
          </div>
        </div>
      </section>

      {/* Services Overview with scroll animation */}
      <section 
        ref={servicesAnimation.ref}
        className="py-16 md:py-24 bg-slate-50" 
        data-testid="services-overview-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 transform transition-all duration-700 ${servicesAnimation.isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading mb-4">
              {t('services.title')}
            </h2>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className={`bg-white border-0 shadow-md hover:shadow-xl transition-all duration-300 border-t-4 border-t-primary-blue transform hover:-translate-y-2 ${servicesAnimation.isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary-blue/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <service.icon className="w-6 h-6 text-primary-blue" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {t(service.titleKey)}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {t(service.descKey)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className={`text-center mt-10 transform transition-all duration-700 delay-300 ${servicesAnimation.isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <Link to="/services">
              <Button 
                variant="outline" 
                className="border-primary-blue text-primary-blue hover:bg-primary-blue hover:text-white hover:scale-105 transition-all duration-200"
                data-testid="services-view-all"
              >
                {t('hero.ctaSecondary')}
                <ArrowRight className={`w-4 h-4 ${isRTL ? 'me-2 rotate-180' : 'ms-2'}`} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us with scroll animation */}
      <section 
        ref={whyChooseAnimation.ref}
        className="py-16 md:py-24 bg-white" 
        data-testid="why-choose-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className={`transform transition-all duration-700 ${whyChooseAnimation.isVisible ? 'translate-x-0 opacity-100' : (isRTL ? 'translate-x-10' : '-translate-x-10') + ' opacity-0'}`}>
              <ImageCarousel />
            </div>
            <div className={`${isRTL ? 'text-right' : 'text-left'} transform transition-all duration-700 delay-200 ${whyChooseAnimation.isVisible ? 'translate-x-0 opacity-100' : (isRTL ? '-translate-x-10' : 'translate-x-10') + ' opacity-0'}`}>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading mb-6">
                {t('about.subtitle')}
              </h2>
              <p className="text-base text-slate-600 mb-8 leading-relaxed">
                {t('about.description')}
              </p>
              <div className="space-y-4">
                {[
                  { icon: ShieldCheck, titleKey: 'about.point1Title', descKey: 'about.point1Desc' },
                  { icon: Globe, titleKey: 'about.point2Title', descKey: 'about.point2Desc' },
                  { icon: Truck, titleKey: 'about.point3Title', descKey: 'about.point3Desc' },
                ].map((item, index) => (
                  <div 
                    key={index}
                    className={`flex items-start gap-4 transform transition-all duration-500 ${whyChooseAnimation.isVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'}`}
                    style={{ transitionDelay: `${300 + index * 100}ms` }}
                  >
                    <div className="w-10 h-10 bg-primary-blue/10 rounded-lg flex items-center justify-center flex-shrink-0 hover:bg-primary-blue/20 transition-colors">
                      <item.icon className="w-5 h-5 text-primary-blue" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{t(item.titleKey)}</h3>
                      <p className="text-sm text-slate-600">{t(item.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section with scroll animation */}
      <section 
        ref={ctaAnimation.ref}
        className="py-16 md:py-20 bg-primary-blue overflow-hidden" 
        data-testid="cta-section"
      >
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transform transition-all duration-700 ${ctaAnimation.isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-10 opacity-0 scale-95'}`}>
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-heading mb-4">
            {t('hero.title')}
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('hero.subtitle')}
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-white text-primary-blue hover:bg-slate-100 px-8 hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              data-testid="cta-contact-btn"
            >
              {t('hero.cta')}
              <ArrowRight className={`w-5 h-5 ${isRTL ? 'me-2 rotate-180' : 'ms-2'}`} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
